// Third-party
import React, { Component, ReactElement } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { SortingRule } from 'react-table';

// Assets
import emptyEnterpriseModelsImage from '@/images/design-system-v2/empty-state/emptyModels.png';

// Components
import { Breadcrumb, ControlLinkItem } from '@/views/components/navigation';
import { PrimaryButton } from '../../../components/buttons';
import SearchInput from '../../../components/inputs/SearchInput';
import { EditPortfolioModal, DeletePortfoliosModal } from '../../../components/modals';
import { LoadingSpinner } from '../../../components/common';
import PortfoliosTableV2 from '@/views/components/tables/PortfoliosTableV2';
import PortfolioUploadModal from '@/views/components/modals/portfolio-upload/PortfolioUploadModal';
import DropdownButton from '@/views/components/buttons/DropdownButton';
import EmptySearchResults from '@/views/components/data/DataTableV2/EmptySearchResults';
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';
import EnterpriseDetailsSection from './EnterpriseDetails';
import EnterpriseGroupSettings from '@/views/containers/admin/group/EnterpriseGroupSettings';
import { SecondaryNavigation } from '@/views/components/navigation';
import TablePageLayout from '@/views/containers/layouts/TablePageLayout';
import { Tabs } from '@/views/components/navigation';

// Enums
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '../../../../enums/Portfolio';

// Utils
import { canAccessTaxAnalysis, canAccessUploadCSV } from '@/utils/feature-access';
import { formatMessage, FormattedMessage } from '../../../../utils/i18n';
import { showError } from '../../../../utils/errors';
import { getPortfolioMissingOrInvalidHoldings } from '../../../../utils/portfolio/utils';
import { ENTERPRISE_GROUP_NAME } from '../../../../utils/enterprise';
import { userHasPermissionEnabled, PERMISSIONS } from '../../../../utils/permissions';
import { PortfolioEditorContext } from '@/utils/portfolios';

// Actions
import { ReduxState } from '../../../../redux/modules/types';
import { getEnterprisePortfolios, deleteEnterpriseGroupPortfolios, saveEnterpriseGroupPortfolio } from '../../../../redux/modules/enterprise/admin/portfolios';
import { DELETE_ENTERPRISE_GROUP_PORTFOLIO } from '../../../../redux/modules/enterprise/admin/shared';
import {
  resetPortfolio,
  loadPortfolioForEdit,
  setPortfolioEnterprise,
  savePortfolio,
  setPortfolioEditorContext,
  setPortfolioEditorIsCreatingModel,
} from '@/redux/modules/edit-portfolio';
import { selectIsLoadingEvery } from '../../../../redux/modules/ui/loaders';
import Portfolio from '@/types/portfolio';
import User from '@/types/user';
import EnterpriseAdminGroup from '@/types/enterprise-admin-group';
import GenericContact from '@/types/generic-contact';
import PaginatedPortfolios from '@/types/paginated-portfolios';
import { togglePortfolioEditor } from '@/redux/modules/common/actionCreators';

// Types
import EnterpriseDetailInfo from '@/types/enterprise-detail-info';
import EnterpriseTabOption from '@/types/enterprise-tab-option';
import NavigationItem from '@/types/navigation-item';
import EnterpriseInfos from '@/types/enterprise-infos';

// TODO: @deprecate when we deprecate Legacy
import LegacyEnterprisePortfolios from './LegacyEnterprisePortfolios';
import useThemeVersion from '@/hooks/useThemeVersion';

type State = {
  query: string;
  loading: boolean;
  showDeletePortfolioModal: boolean;
  selectedPortfolioForDelete?: Portfolio[];
  nextPortfoliosToken?: string;
  token?: string;
  sorting: SortingRule[];
  showPortfolioUploadModal: boolean;
};

type ParentProps = {
  adminEnterpriseTabOptions?: EnterpriseTabOption[]; // Optional for now due to legacy components. Will be required after legacy components are removed.
  className?: string;
  enterprise?: EnterpriseDetailInfo; // Optional for now due to legacy components. Will be required after legacy components are removed.
  group?: EnterpriseAdminGroup;
  isGroup?: boolean;
  pills?: NavigationItem[]; // Optional for now due to legacy components. Will be required after legacy components are removed.
  selectedAdminEnterpriseTab?: string; // Optional for now due to legacy components. Will be required after legacy components are removed.
  user: User;
  onSave?: (info: EnterpriseInfos) => Promise<EnterpriseInfos>; // Optional for now due to legacy components. Will be required after legacy components are removed.
};

type StateProps = {
  group: EnterpriseAdminGroup;
  isDeletingEnterprisePortfolio: boolean;
  showEditPortfolioModal: boolean;
};

type DispatchProps = {
  getEnterprisePortfolios: (groupId?: string, query?: string, next?: string, sorting?: SortingRule[]) => Promise<PaginatedPortfolios>;
  loadPortfolioForEdit: (portfolio: Portfolio) => Promise<void>;
  deleteEnterpriseGroupPortfolios: (groupId: string, portfolios: Portfolio[]) => Promise<void>;
  savePortfolio: (portfolio: Portfolio, contact?: GenericContact, enterpriseGroupId?: string) => Promise<Portfolio>;
  resetPortfolio: () => void;
  setPortfolioEnterprise: () => void;
  togglePortfolioEditor: (show: boolean) => void;
  setPortfolioEditorContext: (editorContext: PortfolioEditorContext) => void;
  setPortfolioEditorIsCreatingModel: (isCreatingModel?: boolean) => void;
};

type Props = ParentProps & DispatchProps & StateProps;

class EnterprisePortfolios extends Component<Props, State> {
  private i18nEmptyStatePath = 'EmptyStateCard.EnterpriseModels';

  readonly state: State = {
    query: '',
    loading: false,
    showDeletePortfolioModal: false,
    sorting: [],
    showPortfolioUploadModal: false,
  };

  loadPortfolios = debounce((reset = false) => {
    const { query, token, sorting } = this.state;
    const { group } = this.props;

    const localToken = reset ? undefined : token;
    this.setState({ loading: true });
    this.props.getEnterprisePortfolios(group._id, query, localToken, sorting).then(() => {
      this.setState({ loading: false });
    });
  }, 500);

  componentDidMount() {
    this.loadPortfolios();
  }

  resetPortfolio = () => {
    this.props.resetPortfolio();
    this.props.setPortfolioEnterprise();
  };

  onLoadFolioForEdit = (portfolio: Portfolio) => this.props.loadPortfolioForEdit(portfolio);

  onCreatePortfolio = () => {
    this.resetPortfolio();
    this.props.setPortfolioEditorIsCreatingModel(true);
    this.openEditPortfolioModal();
  };

  onEditPortfolio = (portfolio: Portfolio) => {
    this.props.setPortfolioEditorContext(PortfolioEditorContext.PORTFOLIO);

    if (canAccessTaxAnalysis(this.props.user)) {
      this.props.setPortfolioEditorIsCreatingModel(true);
    }

    this.props
      .loadPortfolioForEdit(portfolio)
      .then(() => {
        this.openEditPortfolioModal();
      })
      .catch(showError);
  };

  openEditPortfolioModal = () => {
    this.props.setPortfolioEditorContext(PortfolioEditorContext.PORTFOLIO);
    this.props.togglePortfolioEditor(true);
  };

  onCloseEditPortfolioModal = () => {
    this.props.resetPortfolio();
    this.props.setPortfolioEditorIsCreatingModel(false);
    this.props.togglePortfolioEditor(false);
  };

  onSavePortfolio = (portfolio: Portfolio): Promise<Portfolio> => {
    const { group } = this.props;

    return this.props.savePortfolio(portfolio, undefined, group._id).then(portfolio => {
      this.onCloseEditPortfolioModal();
      this.loadPortfolios(true);
      return portfolio;
    });
  };

  // Show the Delete Confirmation modal when the user tries to delete a portfolio
  onDelete = (portfolios: Portfolio[]) => {
    this.setState({ selectedPortfolioForDelete: portfolios });
    this.showDeletePortfolioModal();
  };

  onDeleteSelectedPortfolios = (portfolios: Portfolio[]) => {
    this.setState({ selectedPortfolioForDelete: portfolios });
    this.showDeletePortfolioModal();
  };

  showDeletePortfolioModal = () => {
    this.setState({ showDeletePortfolioModal: true });
  };

  hideDeletePortfolioModal = () => {
    this.setState({ showDeletePortfolioModal: false });
  };

  onConfirmDeletePortfolio = () => {
    const { group } = this.props;
    const portfolios = this.state.selectedPortfolioForDelete;

    if (!portfolios) {
      return;
    }

    // Reload enterprise's data after deleting a portfolio
    this.props.deleteEnterpriseGroupPortfolios(group._id, portfolios).then(() => {
      this.hideDeletePortfolioModal();
      this.loadPortfolios(true);
    });
  };

  onCancelDeletePortfolio = () => {
    this.setState({ selectedPortfolioForDelete: undefined });
    this.hideDeletePortfolioModal();
  };

  onPortfoliosSearch = (query: string) => {
    this.setState({ query }, () => {
      this.loadPortfolios(true);
    });
  };

  onLoadMore = () => {
    this.loadPortfolios();
  };

  onSortingChange = (sorting: SortingRule[]) => {
    const { group } = this.props;
    this.setState({ sorting, loading: true });
    this.props.getEnterprisePortfolios(group._id, undefined, undefined, sorting).then(() => {
      this.setState({ loading: false });
    });
  };

  getDisplayedColumns = () => {
    const { user } = this.props;

    return [
      {
        key: PORTFOLIO_TABLE_COLUMNS.NAME,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LAST_MODIFIED,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.MENU_TOGGLE,
      },
    ]
      .filter(col => !col.hidden)
      .map(col => col.key);
  };

  onCanEdit = (portfolio: Portfolio): boolean => !portfolio.availableActions || portfolio.availableActions.edit;

  onCanDelete = (portfolio: Portfolio): boolean => !portfolio.availableActions || portfolio.availableActions.delete;

  createDropdownItems = () => [
    {
      className: 'large-icon',
      node: formatMessage('Comparison.NewModel'),
      icon: 'create-portfolio1',
      onClick: this.onCreatePortfolio,
    },
    {
      className: 'large-icon',
      node: formatMessage('Comparison.UploadCSVFile'),
      icon: 'upload-file',
      onClick: this.handleUploadCSV,
      hidden: !canAccessUploadCSV(this.props.user),
    },
  ];

  handleUploadCSV = () => {
    this.props.setPortfolioEditorIsCreatingModel(true);
    this.showPortfolioUploadModal();
  };

  closePortfolioUploadModal = () => {
    this.setState({ showPortfolioUploadModal: false });
  };

  showPortfolioUploadModal = () => {
    this.setState({ showPortfolioUploadModal: true });
  };

  // using the empty state logic from the portfolios table
  // we want a specific empty state component, not the default from the PortfoliosTable
  getEmptyStateCard = (query: string): JSX.Element =>
    !query ? (
      <EmptyStateCard
        imageAlt="Enterprise models placeholder image"
        imagePath={emptyEnterpriseModelsImage}
        messagePath={`${this.i18nEmptyStatePath}.Message`}
        titlePath={`${this.i18nEmptyStatePath}.Title`}
      />
    ) : (
      <div className="empty-state-card">
        <EmptySearchResults
          searchQuery={query}
          titleToLocalize="PortfoliosTable.EmptyData.NoResults.Title"
          messageToLocalize="PortfoliosTable.EmptyData.NoResults.Message"
        />
      </div>
    );

  renderPortfolioTable = (displayedColumns: PORTFOLIO_TABLE_COLUMNS[], displayedMetrics: PORTFOLIO_METRICS_FIELDS[]) => {
    const { group } = this.props;
    const { loading, query } = this.state;

    return (
      <PortfoliosTableV2<Portfolio>
        className={classNames({ 'visually-hidden': loading })}
        displayedColumns={displayedColumns}
        displayedMetrics={displayedMetrics}
        query={query}
        messageIds={{
          deleteMultiple: 'PortfoliosTable.Buttons.DeleteSelectedModels',
          deleteSingle: 'PortfoliosTable.Buttons.DeleteModel',
          editSingle: 'PortfoliosTable.Buttons.EditModel',
        }}
        portfolios={group.portfolios}
        selectedTitle="PortfoliosTable.SelectedTitle.Models"
        canDeletePredicate={portfolio => this.onCanDelete(portfolio)}
        canEditPredicate={portfolio => this.onCanEdit(portfolio)}
        missingProductPredicate={portfolio => getPortfolioMissingOrInvalidHoldings(portfolio).length > 0}
        onDelete={this.onDelete}
        onEdit={this.onEditPortfolio}
      />
    );
  };

  getDetailsSection = () => {
    if (this.props.isGroup) {
      const breadcrumbs = [
        {
          value: formatMessage('Admin.Enterprise.Groups.Title'),
          redirect: '/admin/groups',
        },
        {
          value: this.props.group?.name,
        },
      ];
      return (
        <>
          <Breadcrumb replace items={breadcrumbs} />
          <EnterpriseGroupSettings onSave={this.props.onSave} group={this.props.group} user={this.props.user} />
        </>
      );
    }
    return <EnterpriseDetailsSection className="info-element" enterprise={this.props.enterprise} onSave={this.props.onSave} user={this.props.user} />;
  };

  render() {
    const { loading, query, showPortfolioUploadModal, showDeletePortfolioModal, selectedPortfolioForDelete } = this.state;
    const { enterprise, group, isDeletingEnterprisePortfolio, isGroup, showEditPortfolioModal } = this.props;
    const displayedColumns = this.getDisplayedColumns();
    const displayedMetrics = [PORTFOLIO_METRICS_FIELDS.MER, PORTFOLIO_METRICS_FIELDS.YEAR_ONE, PORTFOLIO_METRICS_FIELDS.YEAR_THREE];

    if (group == null || group.portfolios == null) {
      return null;
    }

    const name = isGroup ? group.name : enterprise?.name;
    const subtitle = formatMessage('Models.EnterpriseTitle', { name: name ?? '', count: name?.endsWith('s') ? 1 : 0 });

    return (
      <>
        <TablePageLayout
          detailsElement={this.getDetailsSection()}
          mainClassName="enterprise-models"
          pageTitle="Admin.PageTitle"
          pillsFilterElement={this.props.pills && <SecondaryNavigation className="navigation-pills" items={this.props.pills} />}
          subPageTitle={subtitle ?? ''}
          searchInputElement={
            <SearchInput
              className="search-input-v2"
              value={this.state.query}
              onChange={this.onPortfoliosSearch}
              placeholder={formatMessage('Admin.Enterprise.SearchPortfolios')}
            />
          }
          pageActionsElement={
            <DropdownButton
              toggle={{
                component: PrimaryButton,
                dark: true,
                icon: 'plus-med',
                className: 'create-portfolios primary-v2',
              }}
              align="end"
              items={this.createDropdownItems()}
            >
              <FormattedMessage id="Contacts.ContactTabs.Portfolios.Create" />
            </DropdownButton>
          }
          tabsElement={
            this.props.adminEnterpriseTabOptions && (
              <Tabs className="tabs-v2" selected={this.props.selectedAdminEnterpriseTab}>
                {this.props.adminEnterpriseTabOptions.map(option => (
                  <ControlLinkItem ariaLabel={option.label} key={option.id} route={option.value || '#'} value={option.id}>
                    <FormattedMessage id={option.i18nPath} />
                  </ControlLinkItem>
                ))}
              </Tabs>
            )
          }
          loadingElement={
            <div className="loading-container">
              <LoadingSpinner message={formatMessage('Contacts.ContactTabs.Portfolios.LoadingPortfolios')} />
            </div>
          }
          tableElement={this.renderPortfolioTable(displayedColumns, displayedMetrics)}
          isLoading={loading}
          hasData={group.portfolios.length > 0}
          noDataElement={this.getEmptyStateCard(query)}
        />
        <EditPortfolioModal show={showEditPortfolioModal} onSave={this.onSavePortfolio} onCancel={this.onCloseEditPortfolioModal} />

        <PortfolioUploadModal show={showPortfolioUploadModal} onCancel={this.closePortfolioUploadModal} titleId="Modal.PortfolioUpload.UploadModelPortfolio" />

        <DeletePortfoliosModal
          isDeleting={isDeletingEnterprisePortfolio}
          portfolios={selectedPortfolioForDelete || []}
          show={showDeletePortfolioModal}
          onCancel={this.onCancelDeletePortfolio}
          onConfirm={this.onConfirmDeletePortfolio}
        />
      </>
    );
  }
}

const EnterprisePortfoliosComponent = (props: Props): ReactElement => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <EnterprisePortfolios {...props} />;
  }
  return <LegacyEnterprisePortfolios {...props} />;
};

const mapStateToProps = (state: ReduxState, ownProps: ParentProps) => {
  let { group } = ownProps;

  if (!group) {
    const groups = state.enterprise.admin ? state.enterprise.admin.groups : [];
    group = groups.find(g => g.name === ENTERPRISE_GROUP_NAME) as EnterpriseAdminGroup;
  }

  return {
    group,
    isDeletingEnterprisePortfolio: selectIsLoadingEvery(state, [DELETE_ENTERPRISE_GROUP_PORTFOLIO.type]),
    showEditPortfolioModal: state.comparison.portfolioEditor.isVisible,
  };
};

export default connect(mapStateToProps, {
  deleteEnterpriseGroupPortfolios,
  getEnterprisePortfolios,
  loadPortfolioForEdit,
  resetPortfolio,
  saveEnterpriseGroupPortfolio,
  setPortfolioEnterprise,
  savePortfolio,
  togglePortfolioEditor,
  setPortfolioEditorContext,
  setPortfolioEditorIsCreatingModel,
})(EnterprisePortfoliosComponent);
